import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [content, setContent] = useState(null);
  const [activePage, setActivePage] = useState('home');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const savedToken = localStorage.getItem('cms_token');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchContent();
    }
  }, [isAuthenticated, token]);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        setToken(tokenToVerify);
      } else {
        localStorage.removeItem('cms_token');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok) {
        setToken(data.token);
        setIsAuthenticated(true);
        localStorage.setItem('cms_token', data.token);
        setMessage('Login successful!');
      } else {
        setMessage(data.error || 'Login failed');
      }
    } catch (error) {
      setMessage('Connection error. Make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('cms_token');
    setMessage('Logged out successfully');
  };

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/content`);
      const data = await response.json();
      setContent(data);
    } catch (error) {
      setMessage('Failed to fetch content. Make sure the backend server is running.');
    }
  };

  const handleSave = async (pageData) => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/content/page/${activePage}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(pageData)
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Content saved successfully!');
        fetchContent();
      } else {
        setMessage(data.error || 'Failed to save content');
      }
    } catch (error) {
      setMessage('Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div
          className="admin-background"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/topo_map.png)`
          }}
          aria-hidden="true"
        />
        <div className="login-container">
          <h1>UNI Admin Login</h1>
          <Link to="/" className="btn btn-secondary login-home-btn">Home</Link>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Logging in...' : 'Login'}
            </button>
            {message && <p className="message">{message}</p>}
          </form>
        </div>
      </div>
    );
  }

  if (!content) {
    return <div className="admin-loading">Loading content...</div>;
  }

  const currentPage = content.pages[activePage] || {};

  return (
    <div className="admin">
      <div
        className="admin-background"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/topo_map.png)`
        }}
        aria-hidden="true"
      />
      <div className="admin-header">
        <h1>UNI Site Admin</h1>
        <button onClick={handleLogout} className="btn btn-secondary">Logout</button>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <h3>Pages</h3>
          <button
            className={activePage === 'home' ? 'active' : ''}
            onClick={() => setActivePage('home')}
          >
            Home
          </button>
          <button
            className={activePage === 'about' ? 'active' : ''}
            onClick={() => setActivePage('about')}
          >
            About
          </button>
          <button
            className={activePage === 'services' ? 'active' : ''}
            onClick={() => setActivePage('services')}
          >
            Services
          </button>
        </div>

        <div className="admin-editor">
          <h2>Edit: {activePage.charAt(0).toUpperCase() + activePage.slice(1)}</h2>
          
          {activePage === 'home' && (
            <div className="editor-form">
              <h3>Hero Section</h3>
              <div className="form-group">
                <label>Hero Title</label>
                <input
                  type="text"
                  value={currentPage.heroTitle || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      pages: {
                        ...content.pages,
                        home: { ...currentPage, heroTitle: e.target.value }
                      }
                    });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Hero Subtitle</label>
                <textarea
                  value={currentPage.heroSubtitle || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      pages: {
                        ...content.pages,
                        home: { ...currentPage, heroSubtitle: e.target.value }
                      }
                    });
                  }}
                  rows="3"
                />
              </div>

              <h3>Feature Cards</h3>
              {(currentPage.features || []).map((feature, index) => (
                <div key={feature.id || index} className="card-editor">
                  <h4>Card {index + 1}</h4>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={feature.title || ''}
                      onChange={(e) => {
                        const updatedFeatures = [...(currentPage.features || [])];
                        updatedFeatures[index] = { ...updatedFeatures[index], title: e.target.value };
                        setContent({
                          ...content,
                          pages: {
                            ...content.pages,
                            home: { ...currentPage, features: updatedFeatures }
                          }
                        });
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={feature.description || ''}
                      onChange={(e) => {
                        const updatedFeatures = [...(currentPage.features || [])];
                        updatedFeatures[index] = { ...updatedFeatures[index], description: e.target.value };
                        setContent({
                          ...content,
                          pages: {
                            ...content.pages,
                            home: { ...currentPage, features: updatedFeatures }
                          }
                        });
                      }}
                      rows="3"
                    />
                  </div>
                </div>
              ))}

              <button
                onClick={() => handleSave(content.pages.home)}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activePage === 'about' && (
            <div className="editor-form">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={currentPage.title || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      pages: {
                        ...content.pages,
                        about: { ...currentPage, title: e.target.value }
                      }
                    });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={currentPage.content || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      pages: {
                        ...content.pages,
                        about: { ...currentPage, content: e.target.value }
                      }
                    });
                  }}
                  rows="10"
                />
              </div>
              <button
                onClick={() => handleSave(content.pages.about)}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {message && <p className="message">{message}</p>}
        </div>
      </div>
    </div>
  );
};

export default Admin;
